// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import {
    OpenBackOrderModel,
    OpenBackOrdersApiParameter,
    OpenBackOrdersResultModel,
} from "@steiner/App/Services/OpenBackOrders";
import { Draft } from "immer";

export type OpenBackOrdersState = {
    openBackOrders?: OpenBackOrderModel[];
    getOpenBackOrdersParameter?: OpenBackOrdersApiParameter;
    isLoading?: boolean;
    pagination?: PaginationModel | undefined;
    isSubscribed?: boolean;
};

const initialState: OpenBackOrdersState = {
    isLoading: false,
    isSubscribed: false,
    getOpenBackOrdersParameter: {
        shipToAddress: "-1",
        orderNumber: "",
        releaseNumber: "",
    },
};

const xOpenBackOrders = createTypedReducerWithImmer(initialState, {
    "xOpenBackOrders/BeginLoadingOpenBackOrders": (
        draft: Draft<OpenBackOrdersState>,
        action: { isLoading: boolean },
    ) => {
        draft.isLoading = action.isLoading;
        draft.openBackOrders = [];
        draft.pagination = undefined;
    },
    "xOpenBackOrders/SetLoading": (
        draft: Draft<OpenBackOrdersState>,
        action: { isLoading: boolean },
    ) => {
        draft.isLoading = action.isLoading;
    },
    "xOpenBackOrders/FailedLoadingOpenBackOrders": (
        draft: Draft<OpenBackOrdersState>,
    ) => {
        draft.isLoading = false;
    },
    "xOpenBackOrders/CompleteLoadingOpenBackOrders": (
        draft: Draft<OpenBackOrdersState>,
        action: { value: OpenBackOrdersResultModel },
    ) => {
        draft.openBackOrders = action.value.openBackOrderList;
        draft.pagination = action.value.pagination;
        draft.isLoading = false;
        draft.isSubscribed = action.value.isSubscribedToOpenBackOrderEmail;
    },
    "xOpenBackOrders/UpdateLoadOpenBackOrdersParameter": (
        draft: Draft<OpenBackOrdersState>,
        action: { value: OpenBackOrdersApiParameter },
    ) => {
        draft.getOpenBackOrdersParameter = action.value;
    },
});

(reducers as any).xOpenBackOrders = xOpenBackOrders;

export type xOpenBackOrdersState = {
    xOpenBackOrders: OpenBackOrdersState;
} & ApplicationState;

type xOpenBackOrdersReducer = {
    openBackOrdersData: typeof xOpenBackOrders;
};

export type xOpenBackOrdersDataActions =
    | Parameters<xOpenBackOrdersReducer[keyof xOpenBackOrdersReducer]>[1]
    | AnyAction;
